import { FileText, Loader2 } from 'lucide-react';
import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';

interface DocumentLoadingStateProps {
  message?: string;
  subtitle?: string;
  variant?: 'document' | 'template' | 'processing';
  showSkeleton?: boolean;
}

export const DocumentLoadingState: React.FC<DocumentLoadingStateProps> = ({
  message = 'Loading document...',
  subtitle = 'This may take a few moments',
  variant = 'document',
  showSkeleton = true,
}) => {
  const getIconStyle = () => {
    switch (variant) {
      case 'template':
        return 'bg-blue-50 border-blue-100 text-blue-600';
      case 'processing':
        return 'bg-green-50 border-green-100 text-green-600';
      default:
        return 'bg-blue-50 border-blue-100 text-blue-600';
    }
  };

  const renderSkeleton = () => {
    if (!showSkeleton) return null;

    return (
      <div className="w-full max-w-2xl space-y-4">
        {/* Page header skeleton */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>

        {/* Main content skeleton */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] py-12">
      {/* Document Icon with Animation */}
      <div className="relative mb-6">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-2xl border ${getIconStyle()}`}
        >
          <FileText className="h-8 w-8" />
        </div>
        <div className="absolute -top-1 -right-1">
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
        </div>
      </div>

      {/* Loading Message */}
      <p className="text-sm font-medium text-gray-900 mb-2">{message}</p>
      <p className="text-xs text-gray-500 mb-8">{subtitle}</p>

      {/* Document Preview Skeleton */}
      {renderSkeleton()}
    </div>
  );
};
